import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Eyebrow } from "./Reveal";
import { ICON_MAP } from "../data";
import { useContent } from "../ContentContext";
import { TreePine } from "lucide-react";

// SEO landing pages surfaced from the home services grid. Kept as a constant
// so the visual layout stays identical to what shipped originally — this only
// adds a small "learn more" strip beneath the CTA.
const SERVICE_LINKS = [
  { to: "/emergency-tree-service-huntsville-al", label: "Emergency tree service in Huntsville, AL" },
  { to: "/tree-removal-huntsville-al", label: "Tree removal in Huntsville, AL" },
  { to: "/storm-damage-tree-removal-huntsville-al", label: "Storm damage tree removal in Huntsville, AL" },
  { to: "/tree-trimming-huntsville-al", label: "Tree trimming & pruning in Huntsville, AL" },
];

export default function Services({ onEstimate }) {
  const { services } = useContent();
  return (
    <section id="services" data-testid="services-section" className="py-24 md:py-32 noise relative"
             style={{ background: "var(--pine)" }}>
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        <div className="max-w-3xl mb-16">
          <Eyebrow>What We Do</Eyebrow>
          <h2 className="font-display font-extrabold uppercase tracking-tight text-4xl sm:text-5xl leading-[0.95]"
              style={{ color: "var(--moss)" }}>
            Tree service in
            <span className="font-serif-a italic normal-case font-medium" style={{ color: "var(--brass)" }}> Huntsville, AL</span>
          </h2>
          <p className="mt-5 text-base leading-relaxed" style={{ color: "rgba(240,235,225,0.75)" }}>
            Tree removal, trimming, storm damage clean-up and 24/7 emergency tree service for homeowners
            and businesses across Huntsville, Madison and the surrounding North Alabama communities.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {services.map((s, i) => {
            const Icon = ICON_MAP[s.icon] || TreePine;
            const isEmergency = /emergency/i.test(s.title);
            return (
              <motion.div key={s.id || i} data-testid={`service-card-${i}`}
                initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: (i % 3) * 0.08, ease: [0.22, 1, 0.36, 1] }}
                whileHover={{ y: -6 }}
                className="group relative p-7 rounded-2xl border transition-colors duration-300"
                style={{ background: isEmergency ? "rgba(176,32,25,0.22)" : "rgba(29,62,46,0.35)",
                         borderColor: isEmergency ? "var(--sun)" : "rgba(240,235,225,0.1)" }}>
                {isEmergency && (
                  <span className="absolute top-4 right-4 text-[10px] font-extrabold uppercase px-2 py-1 rounded-full"
                        style={{ background: "var(--sun)", color: "var(--pine)" }}>24/7</span>
                )}
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5"
                     style={{ background: isEmergency ? "rgba(244,206,10,0.2)" : "rgba(46,158,67,0.18)" }}>
                  <Icon size={22} style={{ color: isEmergency ? "var(--sun)" : "var(--brass)" }} />
                </div>
                <h3 className="font-display font-bold uppercase tracking-tight text-lg" style={{ color: "var(--moss)" }}>{s.title}</h3>
                <p className="mt-2 text-sm leading-relaxed" style={{ color: "rgba(240,235,225,0.68)" }}>{s.desc}</p>
              </motion.div>
            );
          })}
        </div>

        <div className="mt-14 text-center">
          <button data-testid="services-estimate-btn" onClick={onEstimate}
                  className="rounded-full px-8 py-4 font-semibold transition-transform hover:scale-105"
                  style={{ background: "var(--sun)", color: "var(--pine)" }}>
            Request a Free Estimate
          </button>
        </div>

        {/* Local-SEO landing page links (visually low-key, preserves original layout) */}
        <nav aria-label="Service pages" className="mt-12 pt-8 border-t"
             style={{ borderColor: "rgba(240,235,225,0.1)" }}>
          <p className="text-xs uppercase tracking-widest mb-4 text-center"
             style={{ color: "rgba(240,235,225,0.55)" }}>Explore each service</p>
          <ul className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm">
            {SERVICE_LINKS.map((l) => (
              <li key={l.to}>
                <Link to={l.to}
                      data-testid={`home-service-link-${l.to.slice(1)}`}
                      className="underline-offset-4 hover:underline"
                      style={{ color: "var(--brass)" }}>{l.label}</Link>
              </li>
            ))}
          </ul>
        </nav>
      </div>
    </section>
  );
}
